/**
 * Mailbox naming shared by the Inbox, Mailboxes and Pipeline pages.
 *
 * Every email in Comms Management carries a `source_mailbox`. For Graph
 * mailboxes that is the real shared mailbox address; for the SES stream (LOC
 * replies arriving through the webhook) it is a label address on the hub
 * domain, registered in `comms_mailbox` with provider `ses`. The dashboard
 * shows display names wherever it can and falls back to the address.
 */

export interface MailboxRef {
    address: string
    displayName: string | null
    provider: string
    /** Mail arrives on its own (webhook); there is nothing to sweep. */
    live: boolean
    enabled?: boolean
}

/** Key the API uses for emails whose source_mailbox is NULL (SES mail from before the label existed). */
export const UNATTRIBUTED_MAILBOX_KEY = '__unattributed'
export const UNATTRIBUTED_LABEL = 'Not yet attributed to a mailbox'

export const isUnattributed = (address: string | null | undefined) => !address || address === UNATTRIBUTED_MAILBOX_KEY

const byAddress = (mailboxes: MailboxRef[] | undefined, address: string) => mailboxes?.find((m) => m.address.toLowerCase() === address.toLowerCase()) ?? null

/** Display name if registered, otherwise the address itself. */
export const mailboxLabel = (address: string | null | undefined, mailboxes?: MailboxRef[]): string => {
    if (isUnattributed(address)) return UNATTRIBUTED_LABEL
    const found = byAddress(mailboxes, address as string)
    return found?.displayName || (address as string)
}

/** Short form for chips: display name, or the local part of the address. */
export const mailboxShort = (address: string | null | undefined, mailboxes?: MailboxRef[]): string | null => {
    if (!address) return null
    if (address === UNATTRIBUTED_MAILBOX_KEY) return 'unattributed'
    const found = byAddress(mailboxes, address)
    return found?.displayName || address.split('@')[0]
}

export const isLiveMailbox = (address: string | null | undefined, mailboxes?: MailboxRef[]) => Boolean(address && byAddress(mailboxes, address)?.live)

/**
 * Options for a mailbox filter: every registered mailbox plus any address that
 * appears in the data but is not registered (so nothing is hidden). Live
 * (webhook) mailboxes are marked so users know they are not swept.
 */
export const mailboxOptions = (mailboxes: MailboxRef[] | undefined, seenAddresses: (string | null | undefined)[] = []): { value: string; label: string; title?: string }[] => {
    const seen = new Set<string>()
    const out: { value: string; label: string; title?: string }[] = []
    for (const m of mailboxes || []) {
        const key = m.address.toLowerCase()
        if (seen.has(key)) continue
        seen.add(key)
        out.push({ value: m.address, label: `${m.displayName || m.address}${m.live ? ' · live' : ''}`, title: m.address })
    }
    for (const a of seenAddresses) {
        if (!a || a === UNATTRIBUTED_MAILBOX_KEY) continue
        const key = a.toLowerCase()
        if (seen.has(key)) continue
        seen.add(key)
        out.push({ value: a, label: a, title: a })
    }
    return out
}
